const LEASE_STORAGE_PREFIX = 'ql7_push_events_leader_v1:'
const CHANNEL_PREFIX = 'ql7_push_events_v1:'

export const NOTIFICATION_IMPULSE_LEASE_TTL_MS = 12_000
export const NOTIFICATION_IMPULSE_RENEW_MS = 4_000
export const NOTIFICATION_IMPULSE_CLAIM_SETTLE_MS = 120
export const NOTIFICATION_IMPULSE_CLAIM_JITTER_MS = 180
export const NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS = 240

function normalizeAccountId(value) {
  return String(value || '').trim().toLowerCase()
}

function safeJsonParse(value) {
  try {
    const parsed = JSON.parse(String(value || ''))
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function createTabId(root, random) {
  try {
    const uuid = root?.crypto?.randomUUID?.()
    if (uuid) return String(uuid)
  } catch {}
  const entropy = Math.floor(Math.max(0, Number(random?.() || 0)) * 0xFFFFFFFF)
    .toString(36)
    .padStart(7, '0')
  return `ql7tab_${Date.now().toString(36)}_${entropy}`
}

function clampDelay(value, min = 0, max = 60_000) {
  return Math.max(min, Math.min(max, Math.floor(Number(value) || 0)))
}

function resolveBrowserRuntime(runtime = {}) {
  const root = runtime.window || (typeof window !== 'undefined' ? window : null)
  let storage = runtime.storage || null
  if (!storage) {
    try { storage = root?.localStorage || null } catch { storage = null }
  }
  const BroadcastChannelCtor = runtime.BroadcastChannel || root?.BroadcastChannel || null
  return {
    root,
    storage,
    BroadcastChannelCtor,
    now: typeof runtime.now === 'function' ? runtime.now : () => Date.now(),
    random: typeof runtime.random === 'function' ? runtime.random : () => Math.random(),
    setTimeout: runtime.setTimeout || root?.setTimeout?.bind(root) || globalThis.setTimeout,
    clearTimeout: runtime.clearTimeout || root?.clearTimeout?.bind(root) || globalThis.clearTimeout,
  }
}

function fallbackCoordinator({ onLeadershipChange }) {
  let eligible = false
  let stopped = false
  return {
    mode: 'isolated',
    setEligible(next) {
      if (stopped) return
      const normalized = next === true
      if (eligible === normalized) return
      eligible = normalized
      onLeadershipChange?.(eligible)
    },
    isLeader() {
      return !stopped && eligible
    },
    publishImpulse() {},
    stop() {
      if (stopped) return
      stopped = true
      if (eligible) onLeadershipChange?.(false)
      eligible = false
    },
  }
}

export function createNotificationImpulseLeader({
  accountId,
  onLeadershipChange,
  onImpulse,
  runtime,
} = {}) {
  const normalizedAccountId = normalizeAccountId(accountId)
  const resolved = resolveBrowserRuntime(runtime)
  const {
    root,
    storage,
    BroadcastChannelCtor,
    now,
    random,
    setTimeout,
    clearTimeout,
  } = resolved

  if (!normalizedAccountId || !storage || typeof BroadcastChannelCtor !== 'function') {
    return fallbackCoordinator({ onLeadershipChange })
  }

  const scope = encodeURIComponent(normalizedAccountId)
  const storageKey = `${LEASE_STORAGE_PREFIX}${scope}`
  const channelName = `${CHANNEL_PREFIX}${scope}`
  const tabId = createTabId(root, random)

  let channel = null
  try {
    channel = new BroadcastChannelCtor(channelName)
  } catch {
    return fallbackCoordinator({ onLeadershipChange })
  }

  let stopped = false
  let eligible = false
  let leader = false
  let claimTimer = null
  let settleTimer = null
  let renewTimer = null

  const clearTimer = (name) => {
    const timer = name === 'claim' ? claimTimer : name === 'settle' ? settleTimer : renewTimer
    if (timer) clearTimeout(timer)
    if (name === 'claim') claimTimer = null
    else if (name === 'settle') settleTimer = null
    else renewTimer = null
  }

  const readLease = () => {
    let parsed = null
    try { parsed = safeJsonParse(storage.getItem(storageKey)) } catch { parsed = null }
    if (!parsed?.owner) return null
    return {
      owner: String(parsed.owner),
      expiresAt: Number(parsed.expiresAt) || 0,
      updatedAt: Number(parsed.updatedAt) || 0,
    }
  }

  const writeLease = (expiresAt) => {
    const lease = {
      owner: tabId,
      expiresAt: Number(expiresAt) || 0,
      updatedAt: now(),
    }
    try {
      storage.setItem(storageKey, JSON.stringify(lease))
      return true
    } catch {
      return false
    }
  }

  const removeOwnLease = () => {
    try {
      const lease = readLease()
      if (lease?.owner === tabId) storage.removeItem(storageKey)
    } catch {}
  }

  const notifyLeadership = (next) => {
    const normalized = next === true
    if (leader === normalized) return
    leader = normalized
    onLeadershipChange?.(leader)
  }

  const post = (payload) => {
    try { channel?.postMessage?.(payload) } catch {}
  }

  const scheduleClaim = (delayMs = 0) => {
    if (stopped || !eligible || leader) return
    clearTimer('claim')
    claimTimer = setTimeout(() => {
      claimTimer = null
      attemptClaim()
    }, clampDelay(delayMs))
  }

  const scheduleRenew = () => {
    clearTimer('renew')
    if (stopped || !eligible || !leader) return
    renewTimer = setTimeout(() => {
      renewTimer = null
      if (stopped || !eligible || !leader) return
      const lease = readLease()
      if (lease && lease.owner !== tabId && lease.expiresAt > now()) {
        notifyLeadership(false)
        scheduleClaim(Math.max(80, lease.expiresAt - now() + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS)))
        return
      }
      const expiresAt = now() + NOTIFICATION_IMPULSE_LEASE_TTL_MS
      if (!writeLease(expiresAt)) {
        notifyLeadership(false)
        scheduleClaim(500)
        return
      }
      post({ type: 'leader-heartbeat', owner: tabId, expiresAt })
      scheduleRenew()
    }, NOTIFICATION_IMPULSE_RENEW_MS)
  }

  const becomeLeaderIfStillOwner = () => {
    clearTimer('settle')
    if (stopped || !eligible) return
    const lease = readLease()
    if (lease?.owner !== tabId || lease.expiresAt <= now()) {
      notifyLeadership(false)
      const retryAfter = lease?.expiresAt > now()
        ? lease.expiresAt - now() + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS)
        : 80 + Math.floor(random() * NOTIFICATION_IMPULSE_CLAIM_JITTER_MS)
      scheduleClaim(retryAfter)
      return
    }
    notifyLeadership(true)
    post({ type: 'leader-heartbeat', owner: tabId, expiresAt: lease.expiresAt })
    scheduleRenew()
  }

  function attemptClaim() {
    if (stopped || !eligible || leader) return
    const current = readLease()
    const nowMs = now()
    if (current?.owner === tabId && current.expiresAt > nowMs) {
      becomeLeaderIfStillOwner()
      return
    }
    if (current?.owner && current.owner !== tabId && current.expiresAt > nowMs) {
      notifyLeadership(false)
      scheduleClaim(current.expiresAt - nowMs + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
      return
    }

    const claimDelay = Math.floor(random() * NOTIFICATION_IMPULSE_CLAIM_JITTER_MS)
    clearTimer('claim')
    claimTimer = setTimeout(() => {
      claimTimer = null
      if (stopped || !eligible || leader) return
      const latest = readLease()
      const claimNow = now()
      if (latest?.owner && latest.owner !== tabId && latest.expiresAt > claimNow) {
        scheduleClaim(latest.expiresAt - claimNow + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
        return
      }
      const expiresAt = claimNow + NOTIFICATION_IMPULSE_LEASE_TTL_MS
      if (!writeLease(expiresAt)) {
        scheduleClaim(500)
        return
      }
      post({ type: 'leader-candidate', owner: tabId, expiresAt })
      clearTimer('settle')
      settleTimer = setTimeout(() => {
        settleTimer = null
        becomeLeaderIfStillOwner()
      }, NOTIFICATION_IMPULSE_CLAIM_SETTLE_MS)
    }, claimDelay)
  }

  const relinquishLeadership = ({ announce = true } = {}) => {
    clearTimer('claim')
    clearTimer('settle')
    clearTimer('renew')
    const wasLeader = leader
    if (wasLeader) removeOwnLease()
    notifyLeadership(false)
    if (announce && wasLeader) post({ type: 'leader-release', owner: tabId, releasedAt: now() })
  }

  const onChannelMessage = (event) => {
    if (stopped) return
    const message = event?.data
    if (!message || typeof message !== 'object') return
    if (message.type === 'impulse') {
      if (message.owner === tabId) return
      onImpulse?.(message.payload)
      return
    }
    if (!eligible || message.owner === tabId) return
    if (message.type === 'leader-release') {
      scheduleClaim(40 + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
      return
    }
    if (message.type === 'leader-heartbeat' || message.type === 'leader-candidate') {
      const lease = readLease()
      if (lease?.owner && lease.owner !== tabId && lease.expiresAt > now()) {
        if (leader) relinquishLeadership({ announce: false })
        scheduleClaim(lease.expiresAt - now() + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
      }
    }
  }

  const onStorage = (event) => {
    if (stopped || !eligible || event?.key !== storageKey) return
    const lease = readLease()
    if (leader && lease?.owner !== tabId && lease?.expiresAt > now()) {
      relinquishLeadership({ announce: false })
      scheduleClaim(lease.expiresAt - now() + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
      return
    }
    if (!leader && (!lease?.owner || lease.expiresAt <= now())) {
      scheduleClaim(40 + Math.floor(random() * NOTIFICATION_IMPULSE_FAILOVER_JITTER_MS))
    }
  }

  channel.onmessage = onChannelMessage
  try { root?.addEventListener?.('storage', onStorage) } catch {}

  return {
    mode: 'shared',
    setEligible(next) {
      if (stopped) return
      const normalized = next === true
      if (eligible === normalized) {
        if (eligible && !leader) scheduleClaim(0)
        return
      }
      eligible = normalized
      if (!eligible) {
        relinquishLeadership()
        return
      }
      scheduleClaim(0)
    },
    isLeader() {
      return !stopped && eligible && leader
    },
    publishImpulse(payload) {
      if (stopped || !leader) return
      post({ type: 'impulse', owner: tabId, payload })
    },
    stop() {
      if (stopped) return
      relinquishLeadership()
      stopped = true
      try { root?.removeEventListener?.('storage', onStorage) } catch {}
      try { channel?.close?.() } catch {}
      channel = null
    },
  }
}
